// Libs
import React, { useEffect, Fragment } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { useQueryParam, StringParam } from "use-query-params";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";

// Components
import ProgressBar from "../../component/ProgressBar";
import { Emoji } from "shared_components";
import { default as Loadable } from "../../../components/_Shared/commonLoadable";

// Hooks
import usePrevious from "../../utils/hooks/usePrevious";

// Actions
import { setPersonalization } from "../../module/store/personalization";
import { fetchDropshipperData } from "../../../actions/settings";
import { setAlertMessage } from "../../../actions/index";

// Images
import trophy from "../../images/trophy.png";
import checkIcon from "../../images/icon-checkmark.svg";

// i18n
import { Trans, useTranslation } from "react-i18next";

// Styles
import {
  Body,
  Title,
  Check,
  Header,
  Footer,
  IconBg,
  Content,
  Progress,
  Subtitle,
  Unchecked,
  Container,
  Description,
  CheckPoints,
  ProductPushedCount,
  ProgressBarSection,
  ProgressNotification,
  ProgressNotificationContainer
} from "./YourGoalsBannerStyle";


const YourGoalsBanner = ({
  completed,
  isFetching,
  setAlertMessage,
  searchPerformed,
  importListCount,
  productListCount,
  setPersonalization,
  goalsNumberOfProduct,
  fetchDropshipperData,
  progressBarPercentage,
  fullProgressBarPercentage
}) => {
  const keywords = useQueryParam("keywords", StringParam)[0];
  const { t } = useTranslation();

  const prevRenderState = usePrevious({
    importListCount,
    productListCount,
    searchPerformed,
    progressBarPercentage,
    fullProgressBarPercentage
  });

  useEffect(() => fetchDropshipperData(), []);

  useEffect(() => keywords && fetchDropshipperData(), [keywords]);

  useEffect(
    () => {
      if (searchPerformed) {
      }
    },
    [searchPerformed]
  );

  useEffect(
    () => {
      // Checking if prevAmount hook exists.
      if (prevRenderState !== undefined && completed === false) {
        if (
          importListCount > prevRenderState.importListCount &&
          importListCount <= goalsNumberOfProduct
        ) {
          triggerNotification("new product imported");
        }

        if (
          productListCount > prevRenderState.productListCount &&
          productListCount <= goalsNumberOfProduct
        ) {
          triggerNotification("new product pushed");
        }

        if (
          prevRenderState.searchPerformed !== searchPerformed &&
          searchPerformed
        ) {
          triggerNotification("first search completed");
        }
      }
    },
    [importListCount, productListCount, searchPerformed]
  );

  const getPlusProgress = () => {
    const plusProgress =
      fullProgressBarPercentage - prevRenderState.fullProgressBarPercentage;
    return plusProgress < 1 ? 1 : Math.round(plusProgress);
  };

  const triggerNotification = msg => {
    return setAlertMessage(
      <ProgressNotificationContainer>
        <CircularProgressbar
          value={progressBarPercentage}
          text={`${progressBarPercentage}%`}
          styles={buildStyles({
            pathColor: "#37b2ac",
            trailColor: "white",
            textColor: "white"
          })}
        />

        <ProgressNotification>
          <span>+{getPlusProgress()}%</span>, {msg}
        </ProgressNotification>
      </ProgressNotificationContainer>,
      "success",
      false,
      true
    );
  };

  return (
    <Fragment>
      {goalsNumberOfProduct && completed === false ? (
        <Loadable active={isFetching}>
          <Container>
            <Description>{ t("YourGoalsBanner.Container.Description") }</Description>

            <Content>
              <Body>
                <img src={trophy} alt="trophy" />

                <Header>
                  <Title>{ t("YourGoalsBanner.Header.Title") }</Title>

                  <Subtitle>{ t("YourGoalsBanner.Header.Subtitle") }</Subtitle>
                </Header>

                <Progress>
                  {progressBarPercentage}% <span>{ t("YourGoalsBanner.Progress.Completed") }</span>
                </Progress>
              </Body>

              <ProgressBarSection>
                <ProductPushedCount>
                  <Trans i18nKey="YourGoalsBanner.ProductPushedCount">
                    <span>Goal: {{ goalsNumberOfProduct: goalsNumberOfProduct }}</span> products pushed
                  </Trans>
                </ProductPushedCount>

                <ProgressBar percentage={progressBarPercentage} />

                <CheckPoints>
                  <div>
                    <Check>
                      <img src={checkIcon} alt="check" />
                      { t("YourGoalsBanner.CheckPoints.JoinSpocket") }
                    </Check>
                    <Check>
                      {importListCount + productListCount >=
                      goalsNumberOfProduct ? (
                        <img src={checkIcon} alt="check" />
                      ) : (
                        <Unchecked />
                      )}
                      { t("YourGoalsBanner.CheckPoints.ProductsImported", { count: (importListCount + productListCount), goalsNumberOfProduct })}
                    </Check>
                  </div>
                  <div>
                    <Check>
                      {searchPerformed ? (
                        <img src={checkIcon} alt="check" />
                      ) : (
                        <Unchecked />
                      )}
                      { t("YourGoalsBanner.CheckPoints.FirstSearch") }
                    </Check>
                    <Check>
                      {productListCount >= goalsNumberOfProduct ? (
                        <img src={checkIcon} alt="check" />
                      ) : (
                        <Unchecked />
                      )}
                      { t("YourGoalsBanner.CheckPoints.ProductsPushed", { count: productListCount, goalsNumberOfProduct })}
                    </Check>
                  </div>
                </CheckPoints>
              </ProgressBarSection>
            </Content>

            {progressBarPercentage === 100 &&
              completed === false && (
                <Footer
                  onClick={async () => {
                    await setPersonalization({ goals: { completed: true } });
                    await fetchDropshipperData();
                  }}
                >
                  <IconBg>
                    <Emoji label="Party Popper" symbol="🎉" />
                  </IconBg>
                  Congratulation! you made it through level one!{" "}
                  <span>Click to close →</span>
                </Footer>
              )}
          </Container>
        </Loadable>
      ) : null}
    </Fragment>
  );
};

function mapStateToProps(state) {
  const {
    progress_bar_perc,
    search_performed,
    import_list_count,
    product_list_count,
    completed
  } = state.settings.dropshipperData;

  const getGoalsNumberOfProduct = () =>
    ((state.settings.dropshipperData || 0).goals || 0).number_of_products || 0;

  return {
    progressBarPercentage: Math.round(progress_bar_perc) || 0,
    fullProgressBarPercentage: progress_bar_perc || 0,
    searchPerformed: search_performed || false,
    importListCount: import_list_count || 0,
    productListCount: product_list_count || 0,
    goalsNumberOfProduct: getGoalsNumberOfProduct(),
    isFetching: state.store.personalization.isFetching,
    completed: completed || false
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    { setPersonalization, fetchDropshipperData, setAlertMessage },
    dispatch
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(YourGoalsBanner);



// WEBPACK FOOTER //
// ./src/newDropshipperApp/component/bannerConductor/YourGoalsBanner.js